/* Hallmark · component-discipline · panel-system
 * Dual Power Feed (A/B) Redundancy Calculator & Failover Verification.
 * Standard Electrical Engineering Compliance: NEC / NFPA 70 80% Continuous Load Derating.
 * Zero raw emojis, crisp monospace typography, token-based styling.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { api } from '../../utils/api';
import {
  Zap,
  Server,
  BarChart2,
  ShieldCheck,
  AlertTriangle,
  AlertOctagon,
  Sliders,
  PowerOff,
  Loader2,
} from 'lucide-react';
import type { RackEquipmentItem } from '../../hooks/useRackWorkspaceData';
import {
  calculateRackPowerRedundancy,
  DEFAULT_VOLTAGE_V,
  DEFAULT_AMPERAGE_A,
  type FeedState,
} from '../../utils/rackPowerRedundancy.logic';
import { PduOutletMatrixPopover } from '../canvas/PduOutletMatrixPopover';
import { resolveFeedLetter } from '../../utils/pduOutletMatrix.logic';

interface PduSummaryItem {
  equipment_id: string | number;
  name?: string;
  total_outlets?: number;
  free_outlets?: number;
  load_pct?: number;
}

interface RackPowerTabProps {
  equipmentList: RackEquipmentItem[];
  maxPower: number | null;
  powerSummary?: {
    rack_id?: number | string;
    pdus?: PduSummaryItem[];
  } | null;
  datacenterCode?: string;
  rackId?: number | string;
  workspace?: string;
  onProvisionSuccess?: () => void;
}

/* ── Section label ──────────────────────────────────────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[9px] uppercase tracking-[0.12em] font-semibold"
      style={{ color: 'var(--text-muted)' }}
    >
      {children}
    </p>
  );
}

/* ── Feed Power Meter with 80% Safety Threshold Line ─────────────────────── */
interface FeedMeterProps {
  feed: FeedState;
  oppositeFeedName: string;
  isSimulatedFailure: boolean;
  failoverSurgeActive: boolean;
  onOpenMatrix?: () => void;
}

function FeedMeter({
  feed,
  oppositeFeedName,
  isSimulatedFailure,
  failoverSurgeActive,
  onOpenMatrix,
}: FeedMeterProps) {
  const { capacity } = feed;

  // Values depending on simulation state
  const activeDrawW = isSimulatedFailure
    ? 0
    : failoverSurgeActive
    ? feed.failoverDrawW
    : feed.normalDrawW;

  const activePct = isSimulatedFailure
    ? 0
    : Math.min(Math.round((activeDrawW / capacity.ratedPowerW) * 100), 120);

  const activeCurrentA = isSimulatedFailure
    ? 0
    : Math.round((activeDrawW / capacity.voltageV) * 10) / 10;

  const failoverPct = Math.min(Math.round((feed.failoverDrawW / capacity.ratedPowerW) * 100), 120);

  // Fill color
  const isTrip = activeDrawW > capacity.ratedPowerW;
  const isOverSafe = activeDrawW > capacity.safeLimitW;

  const barColor = isSimulatedFailure
    ? 'var(--text-muted)'
    : isTrip
    ? 'var(--status-critical)'
    : isOverSafe
    ? 'var(--status-warning)'
    : 'var(--status-normal)';

  return (
    <div
      className="flex-1 rounded-sm p-3 flex flex-col justify-between transition-colors"
      style={{
        background: isSimulatedFailure ? 'var(--bg-panel)' : 'var(--bg-app)',
        border: `1px solid ${
          isSimulatedFailure
            ? 'var(--border-color)'
            : isTrip
            ? 'rgba(239, 68, 68, 0.4)'
            : isOverSafe
            ? 'rgba(245, 158, 11, 0.4)'
            : 'var(--border-color)'
        }`,
      }}
    >
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <span
              className={`w-2 h-2 rounded-full ${
                isSimulatedFailure
                  ? 'bg-slate-600'
                  : feed.id === 'A'
                  ? 'bg-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.6)]'
                  : 'bg-indigo-400 shadow-[0_0_8px_rgba(129,140,248,0.6)]'
              }`}
              aria-hidden
            />
            <span className="font-mono text-xs font-bold text-text-primary tracking-wide">
              {feed.name}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {onOpenMatrix && (
              <button
                type="button"
                onClick={onOpenMatrix}
                className="px-1.5 py-0.5 rounded-xs border border-border bg-bg-panel hover:bg-bg-hover text-cyan-400 hover:text-cyan-300 font-bold text-[9px] font-mono transition-colors cursor-pointer"
                title={`Открыть матрицу розеток для ${feed.name}`}
              >
                Матрица
              </button>
            )}
            <span
              className={`font-mono text-[9px] px-1.5 py-0.5 rounded border uppercase font-bold tracking-wider ${
                isSimulatedFailure
                  ? 'bg-red-950/40 text-red-400 border-red-900/60'
                  : isTrip
                  ? 'bg-red-950/40 text-red-300 border-red-500/60'
                  : isOverSafe
                  ? 'bg-amber-950/40 text-amber-300 border-amber-500/60'
                  : 'bg-slate-900 text-slate-300 border-slate-700'
              }`}
            >
              {isSimulatedFailure
                ? 'ОТКАЗ'
                : isTrip
                ? 'ПЕРЕГРУЗКА'
                : isOverSafe
                ? '> 80% ЛИМИТ'
                : 'В НОРМЕ'}
            </span>
          </div>
        </div>

        <div className="flex items-baseline justify-between mb-2">
          <div className="flex items-baseline gap-1">
            <span
              className="text-xl font-mono font-bold tabular-nums"
              style={{ color: barColor }}
            >
              {activeDrawW}
            </span>
            <span className="text-[10px] font-mono text-text-muted">
              Вт ({activeCurrentA}A)
            </span>
          </div>
          <span className="text-[10px] font-mono tabular-nums text-text-muted">
            {activePct}%
          </span>
        </div>

        {/* Meter Gauge Container */}
        <div className="relative mb-2">
          {/* Base bar */}
          <div
            className="w-full rounded-[2px] overflow-hidden relative"
            style={{
              height: 12,
              background: 'var(--bg-input)',
              border: '1px solid var(--border-color)',
            }}
            role="progressbar"
            aria-valuenow={activePct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${feed.name}: ${activePct}% нагрузки`}
          >
            {/* Failover Ghost Surge (visible in normal view to show potential surge) */}
            {!isSimulatedFailure && !failoverSurgeActive && feed.failoverDrawW > feed.normalDrawW && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${Math.min(failoverPct, 100)}%`,
                  background:
                    feed.failoverDrawW > capacity.safeLimitW
                      ? 'repeating-linear-gradient(45deg, rgba(245,158,11,0.2), rgba(245,158,11,0.2) 4px, rgba(245,158,11,0.35) 4px, rgba(245,158,11,0.35) 8px)'
                      : 'repeating-linear-gradient(45deg, rgba(100,116,139,0.2), rgba(100,116,139,0.2) 4px, rgba(100,116,139,0.35) 4px, rgba(100,116,139,0.35) 8px)',
                  transition: 'width 400ms ease',
                }}
                title={`Потенциальная нагрузка при отказе ${oppositeFeedName}: ${feed.failoverDrawW} Вт (${failoverPct}%)`}
              />
            )}

            {/* Active load bar */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${Math.min(activePct, 100)}%`,
                background: barColor,
                transition: 'width 400ms ease',
              }}
            />

            {/* 80% Safety Limit Line (NEC/NFPA 70 continuous threshold) */}
            <div
              style={{
                position: 'absolute',
                left: '80%',
                top: 0,
                bottom: 0,
                width: 2,
                background: 'rgba(239, 68, 68, 0.75)',
                zIndex: 10,
              }}
              title={`Порог безопасности 80% (NEC/NFPA 70): ${capacity.safeLimitW} Вт`}
            />
          </div>

          {/* Marker Labels below bar */}
          <div className="flex justify-between text-[8px] font-mono text-text-muted mt-1 select-none">
            <span>0%</span>
            <span
              className="font-bold text-amber-500/80"
              style={{ paddingLeft: '45%' }}
            >
              80% NEC ({capacity.safeLimitW}W)
            </span>
            <span>100% ({capacity.ratedPowerW}W)</span>
          </div>
        </div>
      </div>

      {/* Failover summary block */}
      <div
        className="mt-2 pt-2 border-t text-[10px] font-mono flex flex-col gap-1"
        style={{ borderColor: 'var(--border-color)' }}
      >
        <div className="flex justify-between items-center">
          <span className="text-text-muted">При отказе {oppositeFeedName}:</span>
          <span
            className={`font-bold tabular-nums ${
              feed.failoverExceedsTrip
                ? 'text-red-400'
                : feed.failoverExceedsSafe
                ? 'text-amber-400'
                : 'text-emerald-400'
            }`}
          >
            {feed.failoverDrawW} Вт ({failoverPct}%)
          </span>
        </div>

        <div className="flex justify-between items-center text-[9px] text-text-muted">
          <span>Номинал ввода:</span>
          <span>
            {capacity.ratedAmperageA}A · {capacity.voltageV}V
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────────────── */
export function RackPowerTab({
  equipmentList,
  maxPower,
  powerSummary,
  datacenterCode,
  rackId,
  workspace,
  onProvisionSuccess,
}: RackPowerTabProps) {
  const queryClient = useQueryClient();

  // Feed configuration state
  const [voltageV] = useState<number>(DEFAULT_VOLTAGE_V);
  const [selectedAmperage, setSelectedAmperage] = useState<number>(() => ((maxPower ?? 0) > 4000 ? 32 : DEFAULT_AMPERAGE_A));
  const [simulationState, setSimulationState] = useState<'normal' | 'trip_a' | 'trip_b'>('normal');

  // 2N Provisioning state
  const [provisionPreset, setProvisionPreset] = useState<'24_c13' | '20_c13_4_c19'>('24_c13');
  const [provisionAmperage, setProvisionAmperage] = useState<number>(32);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [selectedPduForMatrix, setSelectedPduForMatrix] = useState<RackEquipmentItem | null>(null);

  useEffect(() => {
    setSelectedAmperage((maxPower ?? 0) > 4000 ? 32 : DEFAULT_AMPERAGE_A);
  }, [maxPower]);

  const pduCount = useMemo(() => {
    if (powerSummary?.pdus && powerSummary.pdus.length > 0) {
      return powerSummary.pdus.length;
    }
    return equipmentList.filter(e => e.equipment_type?.toLowerCase() === 'pdu').length;
  }, [powerSummary, equipmentList]);

  const existingLeftPdu = useMemo(() => {
    return equipmentList.find(
      e => e.equipment_type?.toLowerCase() === 'pdu' && (e.mounting_position?.toLowerCase() === 'left' || e.name?.toLowerCase().includes('pdu-a'))
    );
  }, [equipmentList]);

  const existingRightPdu = useMemo(() => {
    return equipmentList.find(
      e => e.equipment_type?.toLowerCase() === 'pdu' && (e.mounting_position?.toLowerCase() === 'right' || e.name?.toLowerCase().includes('pdu-b'))
    );
  }, [equipmentList]);

  const pduA = useMemo(() => {
    return (
      equipmentList.find(
        e => (e.equipment_type?.toLowerCase() === 'pdu' || e.is_zero_u) && resolveFeedLetter(e) === 'A'
      ) || existingLeftPdu || null
    );
  }, [equipmentList, existingLeftPdu]);

  const pduB = useMemo(() => {
    return (
      equipmentList.find(
        e => (e.equipment_type?.toLowerCase() === 'pdu' || e.is_zero_u) && resolveFeedLetter(e) === 'B'
      ) || existingRightPdu || null
    );
  }, [equipmentList, existingRightPdu]);

  const handleProvision2N = async () => {
    if (!datacenterCode || !rackId) {
      toast.error('Не указан идентификатор стойки или датацентра');
      return;
    }
    setIsProvisioning(true);
    try {
      await api.post(`/api/datacenters/${datacenterCode}/racks/${rackId}/provision-2n-pdus`, {
        preset_id: provisionPreset,
        rated_amperage: provisionAmperage,
        workspace: workspace || 'master',
      });
      const wasSingle = Boolean(existingLeftPdu || existingRightPdu);
      toast.success(
        wasSingle
          ? 'Стойка успешно дооснащена резервным PDU для обеспечения 2N'
          : 'Стойка успешно оснащена парными PDU A/B (2N)'
      );
      queryClient.invalidateQueries({ queryKey: ['rackEquipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipmentPorts'] });
      queryClient.invalidateQueries({ queryKey: ['powerSummary'] });
      queryClient.invalidateQueries({ queryKey: ['rackPower'] });
      queryClient.invalidateQueries({ queryKey: ['rackConfig'] });
      queryClient.invalidateQueries({ queryKey: ['racks'] });
      onProvisionSuccess?.();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Ошибка при оснащении стойки PDU');
    } finally {
      setIsProvisioning(false);
    }
  };

  // Compute power redundancy using domain logic
  const redundancy = useMemo(() => {
    return calculateRackPowerRedundancy(equipmentList, {
      amperageA: selectedAmperage,
      amperageB: selectedAmperage,
      voltageV,
      rackMaxPowerW: maxPower ?? undefined,
    });
  }, [equipmentList, selectedAmperage, voltageV, maxPower]);

  const { totalLoadW, feedA, feedB, status, alerts, consumers, consumerCounts } = redundancy;

  return (
    <div className="flex flex-col gap-4 text-xs font-sans select-none">

      {/* ── 1. Redundancy / SPOF Status Banner ── */}
      <div
        className={`rounded-sm p-3 border flex items-start gap-3 transition-colors ${
          status === 'critical_overload'
            ? 'bg-red-950/20 border-red-500/50'
            : status === 'spof_risk'
            ? 'bg-amber-950/20 border-amber-500/50'
            : 'bg-emerald-950/20 border-emerald-500/40'
        }`}
      >
        <div className="shrink-0 mt-0.5">
          {status === 'critical_overload' ? (
            <AlertOctagon size={16} className="text-red-400" aria-hidden />
          ) : status === 'spof_risk' ? (
            <AlertTriangle size={16} className="text-amber-400" aria-hidden />
          ) : (
            <ShieldCheck size={16} className="text-emerald-400" aria-hidden />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                status === 'critical_overload'
                  ? 'bg-red-900/40 text-red-300 border-red-700/60'
                  : status === 'spof_risk'
                  ? 'bg-amber-900/40 text-amber-300 border-amber-700/60'
                  : 'bg-emerald-900/40 text-emerald-300 border-emerald-700/60'
              }`}
            >
              {status === 'critical_overload'
                ? '[ АВАРИЯ: СБОЙ ПРИ FAILOVER ]'
                : status === 'spof_risk'
                ? '[ SPOF: РИСК КАСКАДНОГО ОТКЛЮЧЕНИЯ ]'
                : '[ 2N РЕЗЕРВИРОВАНИЕ: ОБЕСПЕЧЕНО ]'}
            </span>
          </div>

          <p className="text-[11px] leading-relaxed text-text-secondary">
            {status === 'critical_overload'
              ? 'Отказ одного из вводов приведет к превышению 100% мощности и срабатыванию автомата на резервном вводе. Требуется срочная балансировка нагрузки!'
              : status === 'spof_risk'
              ? 'При отказе одного ввода нагрузка на оставшийся превысит допустимый предел длительной нагрузки 80% (NEC/NFPA 70). Риск отключения по тепловой перегрузке.'
              : 'Оба ввода имеют достаточный запас. При отказе любого ввода оставшийся ввод гарантированно выдержит 100% критической нагрузки в пределах безопасного порога 80%.'}
          </p>

          {alerts.length > 0 && (
            <ul className="mt-2 space-y-1 text-[10px] font-mono text-text-primary">
              {alerts.map((alt, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <span className="text-status-warning shrink-0">•</span>
                  <span>{alt}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── 2. 2N Redundant PDU Provisioning Card (Tier III Compliance) ── */}
      {pduCount < 2 && (
        <div
          data-testid="pdu-2n-provisioning-card"
          className="rounded-sm p-3.5 border transition-all"
          style={{
            background: 'var(--bg-app)',
            borderColor: 'var(--border-color)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          }}
        >
          <div className="flex items-start justify-between gap-3 mb-2.5">
            <div className="flex items-center gap-2">
              <div
                className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0 border"
                style={{
                  background: 'rgba(6, 182, 212, 0.12)',
                  borderColor: 'rgba(6, 182, 212, 0.35)',
                  color: 'var(--accent, #06b6d4)',
                }}
              >
                <Zap size={15} aria-hidden />
              </div>
              <div>
                <h4 className="text-[12px] font-semibold text-text-primary tracking-wide">
                  2N Резервирование питания (Tier III)
                </h4>
                <p className="text-[10px] text-text-muted">
                  {pduCount === 0
                    ? 'В стойке отсутствуют вертикальные блоки PDU'
                    : 'В стойке обнаружен только 1 PDU (одиночная точка отказа SPOF)'}
                </p>
              </div>
            </div>

            <span
              className="px-2 py-0.5 rounded-sm text-[9px] font-mono font-bold uppercase tracking-wider border shrink-0"
              style={{
                background: 'rgba(245, 158, 11, 0.12)',
                color: '#f59e0b',
                borderColor: 'rgba(245, 158, 11, 0.4)',
              }}
            >
              {pduCount === 0 ? 'PDU: 0 / 2' : 'PDU: 1 / 2 (SPOF)'}
            </span>
          </div>

          <p className="text-[11px] leading-relaxed text-text-secondary mb-3">
            Для обеспечения отказоустойчивости стандарта Tier III стойка требует двух независимых вертикальных вводов (PDU-A в левом канале и PDU-B в правом канале) с индивидуальными группами розеток.
          </p>

          {/* Options: Preset and Rating */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-3">
            {/* Preset selector */}
            <div>
              <label
                htmlFor="pdu-preset-select"
                className="block text-[9px] font-mono uppercase tracking-wider text-text-muted mb-1"
              >
                Конфигурация розеток
              </label>
              <select
                id="pdu-preset-select"
                value={provisionPreset}
                onChange={e => setProvisionPreset(e.target.value as any)}
                disabled={isProvisioning}
                className="w-full text-[11px] px-2 py-1.5 rounded-sm border focus:outline-none transition-colors cursor-pointer"
                style={{
                  background: 'var(--bg-input)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="24_c13">24 × C13 (10A) · Стандарт</option>
                <option value="20_c13_4_c19">20 × C13 + 4 × C19 · Blade / Storage</option>
              </select>
            </div>

            {/* Rated amperage selector */}
            <div>
              <span className="block text-[9px] font-mono uppercase tracking-wider text-text-muted mb-1">
                Номинал автомата ввода
              </span>
              <div className="flex gap-1.5">
                {([16, 32] as const).map(amp => {
                  const isSelected = provisionAmperage === amp;
                  return (
                    <button
                      key={amp}
                      type="button"
                      disabled={isProvisioning}
                      onClick={() => setProvisionAmperage(amp)}
                      className="flex-1 py-1 px-2 rounded-sm text-[11px] font-mono font-bold border transition-colors cursor-pointer"
                      style={{
                        background: isSelected ? 'var(--accent)' : 'var(--bg-input)',
                        color: isSelected ? 'var(--text-on-accent, #ffffff)' : 'var(--text-muted)',
                        borderColor: isSelected ? 'var(--accent)' : 'var(--border-color)',
                      }}
                    >
                      {amp}A ({amp === 16 ? '3.7 кВт' : '7.4 кВт'})
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Planned Allocation Preview */}
          <div
            className="p-2 rounded-sm border mb-3 text-[10px] font-mono flex flex-col gap-1.5"
            style={{
              background: 'var(--bg-panel)',
              borderColor: 'var(--border-color)',
            }}
          >
            <div className="flex justify-between items-center text-text-secondary">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" aria-hidden />
                <span>Левый Zero-U канал (Left):</span>
              </span>
              {existingLeftPdu ? (
                <span className="px-1.5 py-0.5 rounded-xs border border-cyan-800/60 bg-cyan-950/50 text-cyan-300 font-bold text-[9px]">
                  Установлен: {existingLeftPdu.name}
                </span>
              ) : (
                <span className="font-semibold text-text-primary">
                  PDU-A · Ввод A (Фаза L1) · {provisionPreset === '24_c13' ? '24 розетки C13' : '20 C13 + 4 C19'}
                </span>
              )}
            </div>

            <div className="flex justify-between items-center text-text-secondary">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" aria-hidden />
                <span>Правый Zero-U канал (Right):</span>
              </span>
              {existingRightPdu ? (
                <span className="px-1.5 py-0.5 rounded-xs border border-indigo-800/60 bg-indigo-950/50 text-indigo-300 font-bold text-[9px]">
                  Установлен: {existingRightPdu.name}
                </span>
              ) : (
                <span className="font-semibold text-text-primary">
                  PDU-B · Ввод B (Фаза L2) · {provisionPreset === '24_c13' ? '24 розетки C13' : '20 C13 + 4 C19'}
                </span>
              )}
            </div>
          </div>

          {/* Action button */}
          <button
            type="button"
            disabled={isProvisioning || !datacenterCode || !rackId}
            onClick={handleProvision2N}
            className="w-full py-2 px-3 rounded-sm font-semibold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'var(--accent)',
              color: 'var(--text-on-accent, #ffffff)',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
            }}
          >
            {isProvisioning ? (
              <>
                <Loader2 size={14} className="animate-spin" aria-hidden />
                <span>
                  {existingLeftPdu || existingRightPdu
                    ? 'Дооснащение стойки (генерация розеток)...'
                    : 'Оснащение стойки (генерация 48 розеток)...'}
                </span>
              </>
            ) : (
              <>
                <ShieldCheck size={14} aria-hidden />
                <span>
                  {existingLeftPdu && !existingRightPdu
                    ? 'Дооснастить правый ввод PDU-B (2N)'
                    : existingRightPdu && !existingLeftPdu
                    ? 'Дооснастить левый ввод PDU-A (2N)'
                    : 'Оснастить стойку (2N A+B)'}
                </span>
              </>
            )}
          </button>
        </div>
      )}

      {/* ── 3. Dual Feed Power Meters (Ввод A / Ввод B) ── */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap size={13} style={{ color: 'var(--status-warning)' }} aria-hidden />
            <SectionLabel>Питание стойки: Ввод A и Ввод B</SectionLabel>
          </div>

          {/* Rating switcher */}
          <div className="flex items-center gap-1 text-[9px] font-mono">
            <span className="text-text-muted">Автомат:</span>
            {[16, 32].map(amp => (
              <button
                key={amp}
                type="button"
                onClick={() => setSelectedAmperage(amp)}
                className={`px-1.5 py-0.5 rounded transition ${
                  selectedAmperage === amp
                    ? 'bg-accent text-white font-bold'
                    : 'text-text-muted hover:text-text-primary bg-bg-input border border-border'
                }`}
              >
                {amp}A
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FeedMeter
            feed={feedA}
            oppositeFeedName="Ввода B"
            isSimulatedFailure={simulationState === 'trip_a'}
            failoverSurgeActive={simulationState === 'trip_b'}
            onOpenMatrix={pduA ? () => setSelectedPduForMatrix(pduA) : undefined}
          />
          <FeedMeter
            feed={feedB}
            oppositeFeedName="Ввода A"
            isSimulatedFailure={simulationState === 'trip_b'}
            failoverSurgeActive={simulationState === 'trip_a'}
            onOpenMatrix={pduB ? () => setSelectedPduForMatrix(pduB) : undefined}
          />
        </div>
      </div>

      {/* ── 3. Failover Simulation Controls ── */}
      <div
        className="p-3 rounded-sm border flex items-center justify-between gap-3"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        <div className="flex items-center gap-2">
          <Sliders size={13} className="text-cyan-400 shrink-0" aria-hidden />
          <div>
            <div className="text-[10px] font-bold text-text-primary font-mono uppercase">
              Тест симуляции отказа ввода
            </div>
            <div className="text-[9px] text-text-muted">
              Проверьте перенос нагрузки при аварии одного из вводов
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 font-mono text-[9px]">
          <button
            type="button"
            onClick={() => setSimulationState('normal')}
            className={`px-2 py-1 rounded transition border ${
              simulationState === 'normal'
                ? 'bg-slate-700 text-white font-bold border-slate-500'
                : 'text-text-muted hover:text-text-primary border-border bg-bg-input'
            }`}
          >
            Норма (2N)
          </button>
          <button
            type="button"
            onClick={() => setSimulationState('trip_a')}
            className={`px-2 py-1 rounded transition border flex items-center gap-1 ${
              simulationState === 'trip_a'
                ? 'bg-red-950 text-red-300 font-bold border-red-500'
                : 'text-text-muted hover:text-red-400 border-border bg-bg-input'
            }`}
          >
            <PowerOff size={10} aria-hidden />
            Отказ Ввода A
          </button>
          <button
            type="button"
            onClick={() => setSimulationState('trip_b')}
            className={`px-2 py-1 rounded transition border flex items-center gap-1 ${
              simulationState === 'trip_b'
                ? 'bg-red-950 text-red-300 font-bold border-red-500'
                : 'text-text-muted hover:text-red-400 border-border bg-bg-input'
            }`}
          >
            <PowerOff size={10} aria-hidden />
            Отказ Ввода B
          </button>
        </div>
      </div>

      {/* ── 4. Total Rack Summary ── */}
      <div
        className="p-3 rounded-sm border"
        style={{
          background: 'var(--bg-app)',
          borderColor: 'var(--border-color)',
        }}
      >
        <div className="flex justify-between items-center mb-2">
          <span className="text-[10px] font-mono text-text-muted">
            Суммарное энергопотребление стойки
          </span>
          <span className="text-sm font-mono font-bold text-text-primary tabular-nums">
            {totalLoadW} Вт
          </span>
        </div>

        <div className="grid grid-cols-4 gap-2 text-[9px] font-mono text-center pt-2 border-t border-border">
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Dual PSU</div>
            <div className="font-bold text-cyan-400">{consumerCounts.dualPsu} шт</div>
          </div>
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Только Ввод A</div>
            <div className="font-bold text-amber-400">{consumerCounts.singleA} шт</div>
          </div>
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Только Ввод B</div>
            <div className="font-bold text-amber-400">{consumerCounts.singleB} шт</div>
          </div>
          <div className="p-1 rounded bg-bg-input border border-border">
            <div className="text-text-muted">Без питания</div>
            <div className="font-bold text-text-muted">{consumerCounts.unpowered} шт</div>
          </div>
        </div>
      </div>

      {/* ── 4b. PDU Outlets Occupancy ── */}
      {((powerSummary?.pdus && powerSummary.pdus.length > 0) || Boolean(pduA || pduB)) && (
        <div
          className="p-3 rounded-sm border"
          style={{
            background: 'var(--bg-app)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-text-muted">
              Распределение розеток PDU
            </span>
            <span className="text-[10px] font-mono text-cyan-400 font-bold">
              {powerSummary?.pdus?.length || [pduA, pduB].filter(Boolean).length} PDU
            </span>
          </div>
          <div className="space-y-2">
            {powerSummary?.pdus && powerSummary.pdus.length > 0 ? (
              powerSummary.pdus.map((pdu, idx) => {
                const total = pdu.total_outlets ?? 0;
                const free = pdu.free_outlets ?? 0;
                const occupied = Math.max(0, total - free);
                const pct = total > 0 ? Math.round((occupied / total) * 100) : 0;
                const eq = equipmentList.find(e => String(e.id) === String(pdu.equipment_id));
                const pduName = eq?.name || pdu.name || `PDU #${idx + 1}`;
                return (
                  <div key={pdu.equipment_id || idx} className="text-[10px] font-mono">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-text-primary font-semibold truncate max-w-[50%]" title={pduName}>
                        {pduName}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-text-muted tabular-nums">
                          {occupied} / {total} розеток ({pct}%)
                        </span>
                        {eq && (
                          <button
                            type="button"
                            onClick={() => setSelectedPduForMatrix(eq)}
                            className="px-1.5 py-0.5 rounded-xs border border-border bg-bg-panel hover:bg-bg-hover text-cyan-400 hover:text-cyan-300 font-bold text-[9px] transition-colors cursor-pointer"
                            title="Открыть интерактивную матрицу розеток PDU"
                          >
                            Матрица
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-bg-input h-1.5 rounded-full overflow-hidden border border-border">
                      <div
                        className={`h-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              [pduA, pduB].filter(Boolean).map(eq => (
                <div key={eq!.id} className="flex justify-between items-center text-[10px] font-mono py-1">
                  <span className="text-text-primary font-semibold truncate max-w-[60%]" title={eq!.name}>
                    {eq!.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedPduForMatrix(eq!)}
                    className="px-1.5 py-0.5 rounded-xs border border-border bg-bg-panel hover:bg-bg-hover text-cyan-400 hover:text-cyan-300 font-bold text-[9px] transition-colors cursor-pointer"
                    title="Открыть интерактивную матрицу розеток PDU"
                  >
                    Матрица розеток
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── 5. Consumers list with Feed Badges ── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <BarChart2 size={12} style={{ color: 'var(--text-muted)' }} aria-hidden />
          <SectionLabel>Потребители и распределение по вводам</SectionLabel>
        </div>

        {consumers.length === 0 ? (
          <p className="text-xs py-6 text-center text-text-muted">
            Нет оборудования с данными о потреблении
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
            {consumers
              .sort((a, b) => b.powerW - a.powerW)
              .map(c => {
                const isDual = c.psuFeed === 'A+B';
                return (
                  <div
                    key={c.id}
                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-sm border"
                    style={{
                      background: 'var(--bg-app)',
                      borderColor: 'var(--border-color)',
                    }}
                  >
                    <Server size={11} className="shrink-0 text-text-muted" aria-hidden />
                    
                    <span className="flex-1 text-[11px] truncate text-text-primary" title={c.name}>
                      {c.name}
                    </span>

                    {/* Feed allocation badge */}
                    <span
                      className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${
                        isDual
                          ? 'bg-cyan-950/60 text-cyan-300 border-cyan-700/60'
                          : c.psuFeed === 'A'
                          ? 'bg-amber-950/60 text-amber-300 border-amber-700/60'
                          : 'bg-indigo-950/60 text-indigo-300 border-indigo-700/60'
                      }`}
                    >
                      {isDual ? 'ВВОД A+B (2N)' : `ВВОД ${c.psuFeed} (SPOF)`}
                    </span>

                    <span className="text-[10px] font-mono tabular-nums text-text-secondary shrink-0">
                      {c.powerW} Вт
                    </span>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {selectedPduForMatrix && (
        <PduOutletMatrixPopover
          pdu={selectedPduForMatrix}
          allEquipment={equipmentList}
          datacenterCode={datacenterCode}
          rackId={rackId}
          workspace={workspace}
          onClose={() => setSelectedPduForMatrix(null)}
        />
      )}
    </div>
  );
}
